/**
 * Stock Validator - Verifica estoque apenas no momento do checkout
 * Usa event delegation para capturar cliques mesmo em elementos dinâmicos
 */

class StockValidator {
  constructor() {
    this.isValidating = false;
    this.debug = false; // Desativado em produção
    this.log('🚀 StockValidator inicializado');
    this.init();
  }

  log(...args) {
    if (this.debug) {
      console.log('[StockValidator]', ...args);
    }
  }

  error(...args) {
    console.error('[StockValidator ERROR]', ...args);
  }

  init() {
    this.log('📦 Iniciando configuração com EVENT DELEGATION...');
    
    // Usar event delegation no document para capturar TODOS os cliques
    document.addEventListener('click', (e) => {
      const button = e.target.closest('#CartDrawer-Checkout, #checkout, [name="checkout"], .cart__checkout-button');
      
      if (button) {
        this.log('🖱️ Clique capturado via delegation em:', button.id || button.className);
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        this.validateAndProceed(button);
      }
    }, true); // true = capture phase (executa ANTES de outros handlers)

    this.log('✅ Event delegation configurado no document (capture phase)');
  }

  paymentDiscountValue(button) {
    return String(button?.id || '').startsWith('savepay-button-') ? 'yes' : 'no';
  }

  async syncPaymentDiscount(button) {
    const value = this.paymentDiscountValue(button);
    const root = window.Shopify?.routes?.root || '/';
    const normalizedRoot = root.endsWith('/') ? root : root + '/';
    const body = new FormData();
    body.append('attributes[paymentDiscountCheck]', value);

    const response = await fetch(normalizedRoot + 'cart/update.js', {
      method: 'POST',
      headers: { Accept: 'application/json' },
      body,
    });
    if (!response.ok) {
      throw new Error('Cart update failed with status ' + response.status);
    }

    const cart = await response.json();
    if (cart?.attributes?.paymentDiscountCheck !== value) {
      throw new Error('Cart payment preference was not persisted');
    }

    document
      .querySelectorAll('input[name="attributes[paymentDiscountCheck]"]')
      .forEach((input) => {
        input.value = value;
      });
  }

  restoreCheckoutButton(button, originalContent, originalDisabled) {
    button.innerHTML = originalContent;
    button.disabled = originalDisabled;
    button.classList.remove('stock-validating');
  }

  showPaymentPreparationError(button) {
    const message = `
      <div class="stock-warning-message" role="alert">
        <div class="stock-warning-content">
          <strong>Não foi possível preparar o pagamento.</strong>
          <span>Tente novamente.</span>
        </div>
      </div>
    `;
    const footer =
      document.querySelector('.drawer__footer') ||
      document.querySelector('.cart__footer');
    if (footer) {
      footer.insertAdjacentHTML('afterbegin', message);
    } else {
      button.insertAdjacentHTML('beforebegin', message);
    }
  }

  async validateAndProceed(button) {
    this.log('🔄 Iniciando validateAndProceed...');

    if (this.isValidating) {
      this.log('⏳ Já está validando, ignorando clique duplicado');
      return;
    }
    this.isValidating = true;
    this.removeWarningMessages();

    const originalContent = button.innerHTML;
    const originalDisabled = button.disabled;

    this.log('🔄 Alterando botão para estado de loading...');
    button.disabled = true;
    button.classList.add('stock-validating');
    button.innerHTML = '<span class="stock-validator-loading">Verificando...</span>';

    try {
      try {
        this.log('💳 Sincronizando preferência de pagamento...');
        await this.syncPaymentDiscount(button);
      } catch (error) {
        this.error('Erro ao preparar o pagamento:', error);
        this.showPaymentPreparationError(button);
        this.restoreCheckoutButton(button, originalContent, originalDisabled);
        return;
      }

      try {
        this.log('📡 Iniciando verificação de estoque...');
        const result = await this.checkStock();

        this.log('📦 Resultado:', result.success ? 'OK' : result.errors.length + ' erro(s)');

        if (result.success) {
          this.log('✅ Estoque OK! Redirecionando para checkout...');
          this.proceedToCheckout(button);
        } else {
          this.log('❌ Problemas de estoque encontrados:', result.errors);
          this.showWarningMessage(result.errors, button);
          this.restoreCheckoutButton(button, originalContent, originalDisabled);
        }
      } catch (error) {
        this.error('Erro na validação:', error);
        this.log('⚠️ Erro capturado, prosseguindo para checkout (fail-safe)...');
        this.proceedToCheckout(button);
      }
    } finally {
      this.isValidating = false;
      this.log('🏁 validateAndProceed finalizado');
    }
  }
  async checkStock() {
    this.log('📡 Buscando carrinho em /cart.js...');
    const cartResponse = await fetch('/cart.js');
    const cart = await cartResponse.json();

    this.log(`🛒 Carrinho carregado: ${cart.items.length} item(ns)`);

    if (cart.items.length === 0) {
      this.log('🛒 Carrinho vazio');
      return { success: true, errors: [] };
    }

    const errors = [];

    this.log('🔍 Verificando estoque de cada item...');
    const results = await Promise.all(
      cart.items.map((item, index) => {
        this.log(`  📦 Item ${index + 1}: ${item.product_title} (qty: ${item.quantity})`);
        return this.checkVariantStock(item);
      })
    );

    results.forEach((result, index) => {
      if (!result.available) {
        this.log(`  ❌ Item ${index + 1} indisponível:`, result.reason);
        errors.push({
          item: cart.items[index],
          reason: result.reason,
          availableQty: result.availableQty
        });
      } else {
        this.log(`  ✅ Item ${index + 1} disponível`);
      }
    });

    return {
      success: errors.length === 0,
      errors: errors
    };
  }

  async checkVariantStock(item) {
    try {
      const url = `/products/${item.handle}.js`;
      const response = await fetch(url);
      const product = await response.json();

      const variant = product.variants.find(v => v.id === item.variant_id);
      
      if (!variant) {
        return { available: false, reason: 'not_found', availableQty: 0 };
      }

      // VERIFICAÇÃO PRINCIPAL: Se available é false, está esgotado
      if (!variant.available) {
        return { available: false, reason: 'sold_out', availableQty: 0 };
      }

      // Verificação secundária de quantidade (quando tem controle de estoque)
      if (variant.inventory_management === 'shopify' && variant.inventory_policy === 'deny') {
        if (variant.inventory_quantity !== undefined && variant.inventory_quantity < item.quantity) {
          return {
            available: false,
            reason: 'insufficient_stock',
            availableQty: variant.inventory_quantity
          };
        }
      }

      return { available: true };
    } catch (error) {
      this.error(`Erro ao verificar estoque do item ${item.handle}:`, error);
      return { available: true }; // Fail-safe
    }
  }

  proceedToCheckout(button) {
    this.log('🚀 proceedToCheckout chamado');

    // Retoma o submit nativo da Shopify sem disparar novamente o listener de clique.
    const form = button.form || button.closest('form');
    if (form && typeof form.requestSubmit === 'function') {
      this.log('🛒 Enviando formulário para o checkout nativo');
      button.disabled = false;
      form.requestSubmit(button);
      return;
    }

    this.log('🛒 Formulário indisponível; usando fallback /checkout');
    window.location.assign('/checkout');
  }

  showWarningMessage(errors, button) {
    this.log('⚠️ Exibindo mensagem de aviso...');
    const warningHTML = this.createWarningHTML(errors);
    
    // Tentar inserir no cart drawer footer
    const cartDrawerFooter = document.querySelector('.drawer__footer');
    const cartPageFooter = document.querySelector('.cart__footer');
    
    if (cartDrawerFooter) {
      this.log('📍 Inserindo aviso no Cart Drawer footer');
      const existingWarning = cartDrawerFooter.querySelector('.stock-warning-message');
      if (existingWarning) existingWarning.remove();
      cartDrawerFooter.insertAdjacentHTML('afterbegin', warningHTML);
    } else if (cartPageFooter) {
      this.log('📍 Inserindo aviso na página do carrinho');
      const existingWarning = cartPageFooter.querySelector('.stock-warning-message');
      if (existingWarning) existingWarning.remove();
      const cartBlocks = cartPageFooter.querySelector('.cart__blocks');
      if (cartBlocks) {
        cartBlocks.insertAdjacentHTML('beforebegin', warningHTML);
      } else {
        cartPageFooter.insertAdjacentHTML('afterbegin', warningHTML);
      }
    } else {
      this.log('📍 Fallback: inserindo aviso antes do botão');
      button.insertAdjacentHTML('beforebegin', warningHTML);
    }

    const warningEl = document.querySelector('.stock-warning-message');
    if (warningEl) {
      warningEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }

  removeWarningMessages() {
    const warnings = document.querySelectorAll('.stock-warning-message');
    if (warnings.length > 0) {
      this.log(`🗑️ Removendo ${warnings.length} aviso(s) anterior(es)`);
    }
    warnings.forEach(el => el.remove());
  }

  createWarningHTML(errors) {
    const itemsList = errors.map(error => {
      const itemName = error.item.product_title;
      const variant = error.item.variant_title ? ` (${error.item.variant_title})` : '';

      switch (error.reason) {
        case 'sold_out':
          return `<li><strong>${itemName}${variant}</strong> — Esgotado</li>`;
        case 'insufficient_stock':
          if (error.availableQty === 0) {
            return `<li><strong>${itemName}${variant}</strong> — Esgotado</li>`;
          }
          return `<li><strong>${itemName}${variant}</strong> — Apenas ${error.availableQty} disponível(is), você pediu ${error.item.quantity}</li>`;
        case 'not_found':
          return `<li><strong>${itemName}${variant}</strong> — Produto indisponível</li>`;
        default:
          return `<li><strong>${itemName}${variant}</strong> — Indisponível</li>`;
      }
    }).join('');

    return `
      <div class="stock-warning-message" role="alert">
        <div class="stock-warning-icon">
          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <circle cx="12" cy="12" r="10"></circle>
            <line x1="12" y1="8" x2="12" y2="12"></line>
            <line x1="12" y1="16" x2="12.01" y2="16"></line>
          </svg>
        </div>
        <div class="stock-warning-content">
          <strong>Não foi possível continuar:</strong>
          <ul>${itemsList}</ul>
          <span>Atualize as quantidades ou remova os itens para prosseguir.</span>
        </div>
      </div>
    `;
  }
}

// Estilos
const stockValidatorStyles = document.createElement('style');
stockValidatorStyles.textContent = `
  .stock-validating {
    position: relative;
    pointer-events: none;
  }

  .stock-validator-loading {
    display: inline-flex;
    align-items: center;
    gap: 8px;
  }

  .stock-validator-loading::after {
    content: '';
    width: 14px;
    height: 14px;
    border: 2px solid transparent;
    border-top-color: currentColor;
    border-radius: 50%;
    animation: stockValidatorSpin 0.6s linear infinite;
  }

  @keyframes stockValidatorSpin {
    to { transform: rotate(360deg); }
  }

  .stock-warning-message {
    display: flex;
    align-items: flex-start;
    gap: 12px;
    padding: 16px;
    background: #fff5f5;
    border: 1px solid #e53935;
    border-radius: 8px;
    margin-bottom: 16px;
    animation: stockWarningFadeIn 0.3s ease;
  }

  @keyframes stockWarningFadeIn {
    from {
      opacity: 0;
      transform: translateY(-10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  .stock-warning-icon {
    color: #c62828;
    flex-shrink: 0;
    margin-top: 2px;
  }

  .stock-warning-content {
    flex: 1;
    font-size: 13px;
    color: #333;
  }

  .stock-warning-content strong {
    display: block;
    margin-bottom: 8px;
    color: #c62828;
    font-size: 14px;
  }

  .stock-warning-content ul {
    margin: 0 0 10px 0;
    padding-left: 18px;
    list-style: disc;
  }

  .stock-warning-content li {
    margin-bottom: 6px;
    color: #555;
    line-height: 1.4;
  }

  .stock-warning-content li:last-child {
    margin-bottom: 0;
  }

  .stock-warning-content li strong {
    display: inline;
    color: inherit;
    font-size: inherit;
    margin: 0;
  }

  .stock-warning-content > span {
    display: block;
    font-size: 12px;
    color: #888;
    font-style: italic;
    margin-top: 8px;
  }

  .drawer__footer .stock-warning-message {
    margin: 0 0 16px 0;
  }

  .cart__footer .stock-warning-message {
    margin: 0 0 20px 0;
  }
`;
document.head.appendChild(stockValidatorStyles);

// Inicializar IMEDIATAMENTE (não esperar DOMContentLoaded porque usamos delegation)
window.stockValidator = new StockValidator();
